import { Avatar, Box, Button, Chip, Collapse, Divider, IconButton, TextField, Typography } from "@mui/material";
import { useAppContext } from "../context/AppContext";
import { useTranslation } from "react-i18next";
import { generateColorFromId, getCurrentMonth } from "../utils/utilFunctions";
import { useEffect, useState } from "react";
import { deleteTimeLog, fetchTimeLogs } from "../api/timeLog";
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import ClearIcon from "@mui/icons-material/Clear";
import { useLocation } from "react-router-dom";
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import AddTimeLogDialogWithoutTaskId from "../components/dialogs/AddTimeLogWithoutTaskId";
import { fetchFullProjects } from "../api/projects";

const Team = () => {
    const { employeesAppContext, user, setIsLoading } = useAppContext();
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth())
    const [expandedEmployeeId, setExpandedEmployeeId] = useState(null);
    const [timeLogs, setTimeLogs] = useState([]);
    const [filteredTimeLogs, setFilteredTimeLogs] = useState([]);

    const [sortedEmployees, setSortedEmployees] = useState(null);

    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [openTimeLogDialog, setOpenTimeLogDialog] = useState(false);
    const [timeLogIdToDelete, setTimeLogIdToDelete] = useState(null);
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);

    const location = useLocation();


    async function loadProjects() {
        try {
            const data = await fetchFullProjects();
            setProjects(data.projectsWithDetails);
            setTasks(data.tasks);
        } catch (err) {
            console.log('error: ', err)
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (employeesAppContext && timeLogs) {
            const sorted = sortEmployees(employeesAppContext);
            console.log('sorted: ', sorted);
            setSortedEmployees(sorted);
        }
    }, [employeesAppContext, timeLogs]);

    const sortEmployees = (employees) => {
        return [...employees].sort((a, b) => {
            const employeeA = timeLogs.filter((timelog) => timelog.user_id === a.id);
            const employeeB = timeLogs.filter((timelog) => timelog.user_id === b.id);

            const totalMinutesA = employeeA.reduce((sum, log) => sum + log.logged_minutes, 0);
            const totalMinutesB = employeeB.reduce((sum, log) => sum + log.logged_minutes, 0);
            console.log("a: ", a);
            console.log("totalMinutesA: ", totalMinutesA);
            console.log("b: ", b);
            console.log("totalMinutesb: ", totalMinutesB);

            return totalMinutesA - totalMinutesB;
        });
    };

    useEffect(() => {
        // Parse the selectedDate from the URL query params
        const queryParams = new URLSearchParams(location.search);
        const dateParam = queryParams.get('selectedDate');

        // If there's a selectedDate param, set it in state
        if (dateParam) {
            setSelectedDate(dateParam);
        }
    }, [location.search]);

    const handleToggleExpand = (taskId) => {
        setExpandedEmployeeId((prev) => (prev === taskId ? null : taskId));
    };

    useEffect(() => {
        let filteredLogs = [];

        if (selectedDate && !selectedMonth) {
            // Filter by specific date
            filteredLogs = timeLogs.filter(log => {
                const logDate = new Date(log.logged_date).toISOString().split('T')[0];
                return logDate === selectedDate;
            });
        } else if (selectedMonth && !selectedDate) {
            // Filter by selected month
            const [year, month] = selectedMonth.split("-");
            filteredLogs = timeLogs.filter(log => {
                const logDate = new Date(log.logged_date);
                return (
                    logDate.getFullYear() === Number(year) &&
                    logDate.getMonth() === Number(month) - 1
                );
            });
        } else {
            // Default to current month
            const now = new Date();
            filteredLogs = timeLogs.filter(log => {
                const logDate = new Date(log.logged_date);
                return (
                    logDate.getMonth() === now.getMonth() &&
                    logDate.getFullYear() === now.getFullYear()
                );
            });
        }

        setFilteredTimeLogs(filteredLogs);
    }, [selectedDate, selectedMonth, timeLogs]);

    const loadTimeLogs = async () => {
        try {
            const data = await fetchTimeLogs();
            setTimeLogs(data);
            setFilteredTimeLogs(data);
        } catch (err) {
            console.log('error fetching time logs: ', err);
        } finally {
            setIsLoading(false);
        }
    }
    useEffect(() => {
        setIsLoading(true);
        loadTimeLogs();
        loadProjects();
    }, [])


    const handleConfirmDeleteTimelog = async () => {
        await deleteTimeLog(timeLogIdToDelete);
        loadTimeLogs();
        setTimeLogIdToDelete(null)
    }

    return <Box
        backgroundColor="white"
        sx={{
            display: "flex",
            flexDirection: "column",
            height: "100vh", // Full screen height
        }}
    >

        {/* This will take the remaining height */}
        <Box
            sx={{
                backgroundColor: "#eee",
                margin: 2,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <Box sx={{ height: 60, px: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ marginRight: "10px" }} variant="h5">{t('team')}</Typography>

                    <TextField
                        type="date"
                        size="small"
                        value={selectedDate || ""}
                        onChange={(e) => { setSelectedDate(e.target.value); setSelectedMonth(null) }}
                        InputLabelProps={{
                            shrink: true,
                            sx: {
                                color: "#666",
                                "&.Mui-focused": { color: "#000" },
                            },
                        }}
                        InputProps={{
                            endAdornment: selectedDate && (
                                <IconButton
                                    size="small"
                                    onClick={() => setSelectedDate(null)}
                                    sx={{ ml: "-15px", color: 'black' }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            ),
                            sx: {
                                padding: "0px",
                                "& input": {
                                    padding: "6px 8px",
                                    color: "#000",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#999",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#666",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#333",
                                },
                            },
                        }}
                        sx={{
                            width: "auto",
                            minWidth: 130,
                            maxWidth: 160,
                            mr: 2,
                        }}
                    />
                    <TextField
                        type="month"
                        size="small"
                        value={selectedMonth || ""}
                        onChange={(e) => { setSelectedMonth(e.target.value); setSelectedDate(null) }}
                        InputLabelProps={{
                            shrink: true,
                            sx: {
                                color: "#666",
                                "&.Mui-focused": { color: "#000" },
                            },
                        }}
                        InputProps={{
                            endAdornment: selectedMonth && (
                                <IconButton
                                    size="small"
                                    onClick={() => setSelectedMonth(null)}
                                    sx={{ ml: "-15px", color: 'black' }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            ),
                            sx: {
                                padding: "0px",
                                "& input": {
                                    padding: "6px 8px",
                                    color: "#000",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#999",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#666",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#333",
                                },
                            },
                        }}
                        sx={{
                            width: "auto",
                            minWidth: 130,
                            maxWidth: 160,
                            mr: 2,
                        }}
                    />

                </Box>
                <Box>
                    <Button
                        onClick={() => { setOpenTimeLogDialog(true) }}
                        variant="contained"
                        sx={{
                            bgcolor: "#444",
                            color: "#fff",
                            fontWeight: 500,
                            borderRadius: 2,
                            textTransform: "none",
                            "&:hover": { bgcolor: "#333" },
                        }}
                    >
                        {t("actions.add_activity")}
                    </Button>
                </Box>
            </Box>

            {/* Scrollable Content */}
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: "white",
                    overflowY: "auto",
                    marginLeft: '8px',
                    marginRight: '8px',
                    border: '1px solid lightgray',
                    borderRadius: 1,
                }}
            >
                {sortedEmployees?.map(employee => {
                    const isExpanded = expandedEmployeeId === employee.id;
                    const employeeTimelogs = filteredTimeLogs.filter((timelog) => timelog.user_id === employee.id)
                    return <Box onClick={() => { handleToggleExpand(employee.id) }} key={`team-${employee.id}`} sx={{ minWidth: '100%', display: 'inline-flex', flexDirection: 'column' }}>
                        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 'calc(100% - 32px)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    key={`useSelect${employee.id}${employee.name}`}
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        paddingBottom: '2px',
                                        paddingLeft: '1px',
                                        marginRight: '10px',
                                        fontSize: '1.3rem',
                                        color: 'black',
                                        cursor: 'pointer',
                                        backgroundColor: generateColorFromId(`${employee.id}${employee.id}${employee.id}`),
                                    }}
                                >
                                    {employee.name[0]}
                                </Avatar>
                                <Typography sx={{
                                    width: '200px',
                                    maxWidth: '200px',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden'
                                }} fontWeight={'bold'}>{employee.name}</Typography>
                            </Box>
                            <Box sx={{ width: '150px', marginLeft: '5px' }}>
                                {employee.phone && (
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            maxWidth: '150px',
                                            overflow: 'hidden',
                                            width: '150px',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>{employee.phone}</Typography>
                                )}
                            </Box>
                            <Box sx={{ width: '150px', marginLeft: '5px' }}>
                                {employee.email && (
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            maxWidth: '150px',
                                            overflow: 'hidden',
                                            width: '150px',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>{employee.email}</Typography>
                                )}
                            </Box>
                            <Box sx={{ width: '70px', marginLeft: '5px' }}>
                                {employee.role && (
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            maxWidth: '70px',
                                            overflow: 'hidden',
                                            width: '70px',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>{t(`roles.${employee.role}`)}</Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '90px', maxWidth: "90px", marginLeft: '5px' }}>
                                <Chip
                                    icon={<AccessTimeFilledIcon fontSize="small" sx={{ color: 'black' }} />}
                                    label={
                                        (() => {
                                            const totalMinutes = employeeTimelogs
                                                .reduce((sum, log) => sum + log.logged_minutes, 0);
                                            return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
                                        })()
                                    }
                                    size="small"
                                    sx={{
                                        width: '90px', maxWidth: "90px",
                                        backgroundColor: 'lightgray',
                                        color: 'black',
                                        borderRadius: '16px',
                                        '& .MuiChip-icon': {  // This targets the icon within the Chip
                                            color: 'black',  // Ensures the icon color is black
                                        }
                                    }}
                                >
                                </Chip>

                            </Box>
                        </Box>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, py: 1, backgroundColor: '#f9f9f9' }}>
                                {employeeTimelogs.length > 0 ? (
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {t("common.log_time")}:
                                        </Typography>
                                        {employeeTimelogs.map((log) => {
                                            const canDelete =
                                                user &&
                                                (['owner', 'admin', 'manager'].includes(user.role) || user.id === log.user_id);

                                            return (
                                                <Box key={log.id} sx={{ mb: 1, pl: 1, display: 'flex', alignItems: 'center' }}>

                                                    <Typography variant="body2">
                                                        •
                                                    </Typography>
                                                    {employee && (
                                                        <Avatar
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                marginLeft: '8px',
                                                                marginRight: '8px',
                                                                color: 'black',
                                                                cursor: 'pointer',
                                                                backgroundColor: generateColorFromId(`${employee.id}${employee.id}${employee.id}`),
                                                            }}
                                                        >
                                                            {employee.name[0]} {/* Display first letter of employee's name */}
                                                        </Avatar>
                                                    )}
                                                    <Typography variant="body2" fontWeight={'bold'} sx={{ marginRight: '10px' }}>
                                                        {log.project_title}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={'bold'} sx={{ marginRight: '10px' }}>
                                                        {log.phase_title}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={'bold'} sx={{ marginRight: '10px' }}>
                                                        {log.category_title}
                                                    </Typography>

                                                    <Typography variant="body2">
                                                        {new Date(log.logged_date).toLocaleDateString()} —
                                                        {` `} {Math.floor(log.logged_minutes / 60)}h {log.logged_minutes % 60}m
                                                        ({log.logged_minutes} min)
                                                    </Typography>
                                                    {canDelete && (
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => { setTimeLogIdToDelete(log.id); setOpenConfirmationDialog(true) }}
                                                            sx={{ ml: 1 }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                        <Typography sx={{ fontWeight: 'bold', mt: 1 }}>
                                            Total: {
                                                (() => {
                                                    const totalMinutes = employeeTimelogs
                                                        .reduce((sum, log) => sum + log.logged_minutes, 0);
                                                    return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m (${totalMinutes} min)`;
                                                })()
                                            }
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography>{t('common.no_time_logged')}</Typography>
                                )}
                            </Box>
                        </Collapse>
                        <Divider sx={{ width: "100%", backgroundColor: 'lightgray' }} />
                    </Box>
                })}
            </Box>

            {/* Footer */}
            <Box sx={{ height: 30, px: 1, display: "flex", alignItems: "center" }}>
            </Box>
        </Box>
        <ConfirmationDialog
            open={openConfirmationDialog}
            onClose={() => {
                setTimeLogIdToDelete(null);
                setOpenConfirmationDialog(false)
            }}
            onConfirm={handleConfirmDeleteTimelog}
            title="Are you sure?"
            message="Are you sure you want to delete this item? This action cannot be undone."
        />

        <AddTimeLogDialogWithoutTaskId
            open={openTimeLogDialog}
            onClose={() => { setOpenTimeLogDialog(false) }}
            onSuccess={() => { loadTimeLogs(); setOpenTimeLogDialog(false); }}
            projects={projects}
            defaultLoggedDate={selectedDate}
            tasks={tasks} />
    </Box >;
}
export default Team;